import { useEffect, useRef, useState } from 'react';

/**
 * Holds a boolean loading flag `true` for at least `minMs` milliseconds after
 * it first flips to `true`, even if the underlying flag flips back to `false`
 * before the minimum has elapsed.
 *
 * Prevents loading indicators from flickering on fast networks where a query
 * resolves in 50–150ms — which reads as "nothing happened" to the user and
 * actually feels slower than showing a skeleton for a quarter second.
 *
 * Usage:
 *   const loading = useMinimumLoadingTime(rawLoading, 400);
 *
 * @param {boolean} loading - Raw loading flag (e.g. from Apollo useQuery).
 * @param {number}  minMs   - Minimum time in ms to keep the flag true.
 * @returns {boolean}
 */
export default function useMinimumLoadingTime(loading, minMs = 400) {
  const [held, setHeld] = useState(loading);
  const startedAtRef = useRef(loading ? Date.now() : null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (loading) {
      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setHeld(true);
      return undefined;
    }

    // loading just flipped to false — decide whether to release immediately or
    // hold until the minimum elapses.
    const startedAt = startedAtRef.current;
    if (startedAt === null) {
      setHeld(false);
      return undefined;
    }

    const elapsed = Date.now() - startedAt;
    const remaining = minMs - elapsed;

    if (remaining <= 0) {
      startedAtRef.current = null;
      setHeld(false);
      return undefined;
    }

    timeoutRef.current = setTimeout(() => {
      startedAtRef.current = null;
      timeoutRef.current = null;
      setHeld(false);
    }, remaining);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [loading, minMs]);

  return held;
}
